import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import apiLink from "../../../api/apiLink";
import Loader from "../../../components/common/Loader";
import { smsCount } from "../../../components/common/UtilityMethods";
import { smsSettingUpdateIsp } from "../../../features/authSlice";
import useISPowner from "../../../hooks/useISPOwner";
import { audioFileUpload } from "../../../features/apiCallAdmin";
import AudioUploadPlayer from "../../../pages/settings/template/AudioUploadPlayer";

/* ------------------------------------------------------------------ */
/* Constants                                                           */
/* ------------------------------------------------------------------ */

const MAX_SMS_LENGTH = 334;
const MAX_TITLE_LENGTH = 40;

const DAY_OPTIONS = [
  { n: 1, id: "OneDay", label: "billDueOneDay", tpl: "onedayTemplate" },
  { n: 2, id: "TwoDay", label: "billDueTwoDay", tpl: "twodaysTemplate" },
  { n: 3, id: "ThreeDay", label: "billDueThreeDay", tpl: "threedaysTemplate" },
  { n: 4, id: "FourDay", label: "billDueFourDay", tpl: "fourdaysTemplate" },
  { n: 5, id: "FiveDay", label: "billDueFiveDay", tpl: "fivedaysTemplate" },
  { n: 6, id: "SixDay", label: "billDueSixDay", tpl: "sixdaysTemplate" },
  { n: 7, id: "SevenDay", label: "billDueSevenDay", tpl: "sevendaysTemplate" },
];

const BASE_TEMPLATE_ITEMS = [
  { id: "USERNAME", value: "USER: USERNAME" },
  { id: "CUSTOMER_ID", value: "ID: CUSTOMER_ID" },
  { id: "CUSTOMER_NAME", value: "NAME: CUSTOMER_NAME" },
  { id: "AMOUNT", value: "BILL: AMOUNT" },
  { id: "BILL_DATE", value: "LAST DATE: BILL_DATE" },
];

const LEGACY_PLACEHOLDERS = [
  "ইউজারনেমঃ USERNAME",
  "ইউজার আইডিঃ USERID",
  "গ্রাহকঃ NAME",
  "বিলঃ AMOUNT",
  "তারিখঃ DATE",
];

const ON_OFF_OPTIONS = [
  { id: "onTemplate", value: "on", labelKey: "ON" },
  { id: "offTemplate", value: "off", labelKey: "OFF" },
];

const dayKey = (n) => `alert${n}`;
const dayNumber = (key) => Number(String(key).replace("alert", ""));

function AlertSmsTemplate() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { ispOwnerData, settings, bpSettings } = useISPowner();

  const resellerId = useSelector(
    (state) => state.persistedReducer.auth.currentUser.reseller.id,
  );

  const [loading, setLoading] = useState(false);

  const [messageType, setMessageType] = useState("");
  const [sendingType, setSendingType] = useState();
  const [billConfirmation, setBillConfirmation] = useState("off");
  const [days, setDays] = useState([]);

  const [alertNum, setAlertNum] = useState("");
  const [title, setTitle] = useState("");
  const [selectedItems, setSelectedItems] = useState([]);
  const [bottomText, setBottomText] = useState("");

  const [audioByDay, setAudioByDay] = useState({});

  const currentFile = audioByDay[alertNum];
  const savedAudio = settings?.sms?.voiceTemplate?.[alertNum];

  const paymentLink = `Payment Link: https://app.one-billing.com/isp/${ispOwnerData?.netFeeId}`;

  const templateItems = useMemo(
    () => [
      ...BASE_TEMPLATE_ITEMS,
      ...(ispOwnerData?.bpSettings?.hasPG
        ? [{ id: "PAYMENT_LINK", value: paymentLink, label: "PAYMENT_LINK" }]
        : []),
    ],
    [ispOwnerData?.bpSettings?.hasPG, paymentLink],
  );

  const messageTypes = useMemo(
    () => [
      { label: t("text"), value: "text" },
      ...(bpSettings?.voiceMessage
        ? [{ label: t("voice"), value: "voice" }]
        : []),
    ],
    [t, bpSettings?.voiceMessage],
  );

  const sendingMessageTypes = useMemo(
    () => [
      { label: t("nonMasking"), value: "nonMasking" },
      { label: t("masking"), value: "masking" },
      { label: t("fixedNumber"), value: "fixedNumber" },
      ...(bpSettings?.hasSmsGateway
        ? [{ label: t("personalGateway"), value: "personalGateway" }]
        : []),
    ],
    [t, bpSettings?.hasSmsGateway],
  );

  const onOffOptions = useMemo(
    () => ON_OFF_OPTIONS.map((o) => ({ ...o, label: t(o.labelKey) })),
    [t],
  );

  const itemsText = selectedItems.join("\n");
  const fullMessage = `${itemsText}${bottomText}`;
  const fixedLength = title.length + itemsText.length;

  const getSavedDays = useCallback(
    (type) =>
      (type === "voice"
        ? settings?.sms?.voiceAlertDays
        : settings?.sms?.alertDays) || [],
    [settings],
  );

  const resetTemplateState = useCallback(() => {
    setAlertNum("");
    setSelectedItems([]);
    setTitle("");
    setBottomText("");
  }, []);

  useEffect(() => {
    const type = settings?.sms?.alertMessageType || "";
    setBillConfirmation(settings?.sms?.alert ? "on" : "off");
    setMessageType(type);
    setSendingType(settings?.sms?.alertSendBy);
    setDays(getSavedDays(type));
  }, [settings, getSavedDays]);

  useEffect(() => {
    if (alertNum && !days.includes(dayNumber(alertNum))) {
      resetTemplateState();
    }
  }, [days, alertNum, resetTemplateState]);

  const handleMessageTypeChange = (type) => {
    setMessageType(type);
    setDays(getSavedDays(type));
    resetTemplateState();
  };

  const toggleDay = (n) =>
    setDays((prev) =>
      prev.includes(n) ? prev.filter((d) => d !== n) : [...prev, n],
    );

  const toggleTemplateItem = (item) => {
    if (selectedItems.includes(item)) {
      setSelectedItems((prev) => prev.filter((i) => i !== item));
      return;
    }

    if (fixedLength + bottomText.length + item.length > MAX_SMS_LENGTH) {
      toast.error(t("exceedSMSLimit"));
      return;
    }
    setSelectedItems((prev) => [...prev, item]);
  };

  const handleTemplateSelect = (key) => {
    if (messageType === "voice") {
      setAlertNum(key);
      return;
    }

    if (!key) {
      resetTemplateState();
      return;
    }

    const parsed = parseTemplate(settings?.sms?.template?.[key] || "", {
      allowedItems: templateItems.map((i) => i.value),
      paymentLink,
    });

    setAlertNum(key);
    setTitle(parsed.title);
    setSelectedItems(parsed.items);
    setBottomText(parsed.bottomText);
  };

  const handleAudioChange = (file) => {
    if (!alertNum) {
      toast.error(t("selectTemplate"));
      return;
    }
    setAudioByDay((prev) => {
      const next = { ...prev };
      if (file) next[alertNum] = file;
      else delete next[alertNum];
      return next;
    });
  };

  const uploadVoiceFile = async (file) => {
    const formData = new FormData();
    formData.append("file", file);

    const oldPublicId = savedAudio?.publicID;
    if (oldPublicId) formData.append("publicId", oldPublicId);

    const uploaded = await audioFileUpload(setLoading, formData);
    const f = uploaded?.[0];
    if (!f) return null;

    return {
      originalName: f.originalName,
      path: f.path,
      publicID: f.publicID,
      duration: f.duration,
    };
  };

  const buildTemplate = ({ title, items, bottomText }) =>
    [title, ...items, bottomText].join("\n");

  const parseTemplate = (raw, { allowedItems, paymentLink }) => {
    let cleaned = raw;
    LEGACY_PLACEHOLDERS.forEach((p) => {
      cleaned = cleaned.replace(p, "");
    });
    cleaned = cleaned.replace("Payment_link", paymentLink);

    const [title = "", ...rest] = cleaned.split("\n");
    const items = rest.filter((line) => allowedItems.includes(line));
    const bottomText = rest
      .filter((line) => line !== "" && !allowedItems.includes(line))
      .map((line) => `${line}\n`)
      .join("");

    return { title, items, bottomText };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!messageType) {
      toast.error(t("messageType"));
      return;
    }

    const isVoice = messageType === "voice";
    const newFile = isVoice ? currentFile : null;

    if (isVoice) {
      if (!alertNum) {
        toast.error(t("selectTemplate"));
        return;
      }
      if (!newFile && !savedAudio) {
        toast.error("Voice file is required.");
        return;
      }
    }

    const data = {
      ...settings?.sms,
      alertMessageType: messageType,
      alertSendBy: sendingType,
      alert: billConfirmation === "on",
      ...(isVoice ? { voiceAlertDays: days } : { alertDays: days }),
    };

    if (!isVoice) {
      const body = buildTemplate({ title, items: selectedItems, bottomText });
      data.template = {
        ...settings?.sms?.template,
        alert: body,
        ...(alertNum ? { [alertNum]: body } : {}),
      };
    }

    setLoading(true);
    try {
      if (newFile) {
        const voice = await uploadVoiceFile(newFile);
        if (!voice) {
          toast.error("Voice file upload failed.");
          return;
        }
        data.voiceTemplate = {
          ...settings?.sms?.voiceTemplate,
          [alertNum]: voice,
        };
      }

      const res = await apiLink.patch(
        `/reseller/settings/sms/${resellerId}`,
        data,
      );
      dispatch(smsSettingUpdateIsp(res.data));
      toast.success(t("alertSMStemplateSaveAlert"));
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  function RadioGroup({ name, options, value, onChange }) {
    return options.map((opt) => {
      const id = `${name}-${opt.value}`;
      return (
        <div className="message_radio" key={opt.value}>
          <input
            type="radio"
            id={id}
            name={name}
            value={opt.value}
            checked={value === opt.value}
            onChange={(e) => onChange(e.target.value)}
          />
          <label htmlFor={id}>{opt.label}</label>
        </div>
      );
    });
  }

  return (
    <div>
      <form onSubmit={handleSubmit} className="settingForm">
        <div className="writeMessageSection">
          <div className="messageStatus d-flex justify-content-between">
            <div className="message-sending-type">
              <h5>{t("messageType")}</h5>
              <div className="smsType">
                <RadioGroup
                  name="messageType"
                  options={messageTypes}
                  value={messageType}
                  onChange={handleMessageTypeChange}
                />
              </div>
            </div>

            <div className="sending-status">
              <h4>{t("alertSMStemplate")}</h4>
              <div className="displayGrid1">
                <RadioGroup
                  name="billConfirmation"
                  options={onOffOptions}
                  value={billConfirmation}
                  onChange={setBillConfirmation}
                />
              </div>
            </div>

            {messageType === "text" && (
              <div className="message-sending-type">
                <h4>{t("sendingMessageType")}</h4>
                <div className="smsType">
                  <RadioGroup
                    name="sendingType"
                    options={sendingMessageTypes}
                    value={sendingType}
                    onChange={setSendingType}
                  />
                </div>
              </div>
            )}
          </div>

          <div>
            <p className="h6 mt-3">{t("dueToFinishBillCycle")}</p>

            <div className="displayGrid d-flex mb-3">
              {DAY_OPTIONS.map((day) => (
                <div className="day_checkbox" key={day.id}>
                  <input
                    type="checkbox"
                    className="getValueUsingClass"
                    id={day.id}
                    checked={days.includes(day.n)}
                    onChange={() => toggleDay(day.n)}
                  />
                  <label htmlFor={day.id}>{t(day.label)}</label>
                </div>
              ))}
            </div>

            <div className="displayGrid">
              <select
                className="form-select mw-100 mt-0"
                value={alertNum}
                onChange={(e) => handleTemplateSelect(e.target.value)}
                style={{ backgroundColor: "#dcdcdc" }}
              >
                <option value="">{t("selectTemplate")}</option>
                {DAY_OPTIONS.filter((day) => days.includes(day.n)).map(
                  (day) => (
                    <option key={day.n} value={dayKey(day.n)}>
                      {t(day.tpl)}
                    </option>
                  ),
                )}
              </select>
            </div>
          </div>

          {messageType === "text" ? (
            <div className="mt-2">
              <div className="billconfirm">
                <div className="showthesequence">
                  <p className="endingText">{title}</p>
                  {selectedItems.map((item) => (
                    <p key={item}>{item}</p>
                  ))}
                  <p className="endingtext">{bottomText}</p>
                </div>

                <div className="displayFlexx">
                  <div>
                    {templateItems.map((item) => (
                      <div className="radioselect" key={item.id}>
                        <input
                          id={item.id}
                          type="checkbox"
                          className="getValueUsingClass"
                          value={item.value}
                          checked={selectedItems.includes(item.value)}
                          onChange={(e) => toggleTemplateItem(e.target.value)}
                        />
                        <label className="templatelabel" htmlFor={item.id}>
                          {item.label ?? item.value}
                        </label>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="displayGrid">
                  <input
                    className="form-control"
                    type="text"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    placeholder={t("title")}
                    maxLength={MAX_TITLE_LENGTH}
                  />
                </div>
              </div>

              <div className="smsCount">
                <span className="smsLength">
                  {t("letter")} {fullMessage.length}
                </span>
                <span>SMS: {smsCount(fullMessage)}</span>
              </div>

              <textarea
                id="messageTextArea"
                rows="6"
                className="form-control mt-4"
                placeholder={t("messageLikhun")}
                value={bottomText}
                maxLength={Math.max(MAX_SMS_LENGTH + 1 - fixedLength, 0)}
                onChange={(e) => setBottomText(e.target.value)}
              />
            </div>
          ) : messageType === "voice" && alertNum ? (
            <AudioUploadPlayer
              key={alertNum}
              file={currentFile}
              savedAudio={savedAudio}
              onFileChange={handleAudioChange}
            />
          ) : (
            <p className="text-muted text-center mt-4">{t("selectTemplate")}</p>
          )}

          <hr />
          <button type="submit" className="btn btn-success" disabled={loading}>
            {loading ? <Loader /> : t("save")}
          </button>
        </div>
      </form>
    </div>
  );
}

export default AlertSmsTemplate;
